import { resolveTheme, webSocketApiUrlsDictionary } from '../constants';
import { getDocuments } from '../services/documents';
import {
  CrossChargeBox,
  CrossTenant,
  OcpiCdr,
  OcpiLocation,
  OcpiPage,
  OcpiSession,
  OcpiToken,
} from '../types/responses/Ocpi';

/** Limite padrão do CS (PaginationOcpi). O endpoint não tem ordenação nem limite máximo. */
export const OCPI_PAGE_LIMIT = 1000;

export interface OcpiQuery {
  dateFrom?: string;
  dateTo?: string;
  chargeBoxUid?: string;
  /** Só no `/sessions`: status calculado pelo CS (ACTIVE, PENDING, COMPLETED, INVALID). */
  status?: string;
  offset?: number;
  limit?: number;
}

export const buildOcpiUrl = (resource: string, query: OcpiQuery = {}): string => {
  const params = Object.entries({ offset: 0, limit: OCPI_PAGE_LIMIT, ...query })
    .filter(([, value]) => value !== undefined && value !== null && value !== '')
    .map(([key, value]) => `${key}=${encodeURIComponent(String(value))}`)
    .join('&');
  return `ocpi/${resource}?${params}`;
};

/**
 * `getDocuments` devolve o corpo quando a resposta é de sucesso e o objeto
 * `{ type: 'error', status }` quando não é. Normaliza as duas formas.
 */
export const toOcpiPage = <T>(data: any, listKey: string): OcpiPage<T> => {
  if (!data || data.type === 'error' || data.error) {
    return { list: [], total: 0, lastPage: true, error: data?.error || 'error' };
  }
  const list: T[] = Array.isArray(data[listKey]) ? data[listKey] : [];
  return {
    list,
    total: typeof data.total === 'number' ? data.total : list.length,
    lastPage: data.lastPage ?? data.isLastPage ?? true,
  };
};

export const getOcpiLocations = async (query?: OcpiQuery): Promise<OcpiPage<OcpiLocation>> =>
  toOcpiPage<OcpiLocation>(await getDocuments(buildOcpiUrl('locations', query)), 'locationOcpiList');

export const getOcpiSessions = async (query?: OcpiQuery): Promise<OcpiPage<OcpiSession>> =>
  toOcpiPage<OcpiSession>(await getDocuments(buildOcpiUrl('sessions', query)), 'sessionOcpiList');

export const getOcpiCdrs = async (query?: OcpiQuery): Promise<OcpiPage<OcpiCdr>> =>
  toOcpiPage<OcpiCdr>(await getDocuments(buildOcpiUrl('cdrs', query)), 'cdrOcpiList');

export interface OcpiTokenResult {
  found: boolean;
  token?: OcpiToken;
  /** 404 do CS: o UID não existe. Falso quando a consulta falhou por outro motivo. */
  notFound: boolean;
}

export const getOcpiToken = async (tokenUid: string): Promise<OcpiTokenResult> => {
  const data: any = await getDocuments(`ocpi/tokens/${encodeURIComponent(tokenUid.trim())}`);
  if (data?.token) return { found: true, token: data.token, notFound: false };
  return { found: false, notFound: data?.status === 404 };
};

/* ---------------------------------------------------------------------------------------------
 * Estações que o tenant logado recebe como parceiro (eMSP).
 *
 * Reproduz a consulta que o mk-ms-ocpi-java faz ao cross para montar o `/locations` de um
 * parceiro (LocationService.listLocations): `GET /charge-boxs?is_ocpi=true&tenant_external_id=`
 * com o `tenant_external_id` do parceiro, que o cross resolve pelos vínculos (tenant bind) em que
 * ele é a origem. O `tenant_external_id` sai do próprio cross, pelo id do tenant, que no cross é a
 * api-key do CS. Tudo pelo gateway, como os outros microsserviços do dashboard.
 * ------------------------------------------------------------------------------------------- */

export const CROSS_PAGE_SIZE = 500;
const CROSS_MAX_PAGES = 20;

const crossBaseUrl = (): string => {
  const gateway = webSocketApiUrlsDictionary[resolveTheme()]?.gatewayUrl || process.env.REACT_APP_API_GATEWAY_URL;
  return `${gateway}/mk-ms-tenant-cross`;
};

const crossHeaders = (tenantHeader?: string): Record<string, string> => {
  const token = localStorage.getItem('auth-token');
  return {
    Accept: 'application/json',
    Platform: 'DASHBOARD',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(tenantHeader ? { 'X-TENANT-ID': tenantHeader } : {}),
  };
};

export const buildPartnerStationsUrl = (tenantExternalId: string, page: number): string =>
  `/charge-boxs?is_ocpi=true&tenant_external_id=${encodeURIComponent(tenantExternalId)}&page=${page}&size=${CROSS_PAGE_SIZE}`;

export type PartnerFeedResult =
  | { status: 'ok'; tenant: CrossTenant; stations: CrossChargeBox[]; truncated: boolean }
  | { status: 'no-external-id'; tenant?: CrossTenant }
  | { status: 'error'; httpStatus?: number };

const fetchJson = async (url: string, headers: Record<string, string>): Promise<{ status: number; body?: any }> => {
  try {
    const response = await fetch(url, { headers });
    if (!response.ok) return { status: response.status };
    return { status: response.status, body: await response.json() };
  } catch {
    return { status: 0 };
  }
};

export const getPartnerFeedStations = async (): Promise<PartnerFeedResult> => {
  const apiKey = localStorage.getItem('api-key');
  if (!apiKey) return { status: 'error' };

  const tenantResponse = await fetchJson(`${crossBaseUrl()}/tenants/${apiKey}`, crossHeaders(apiKey));
  if (!tenantResponse.body) return { status: 'error', httpStatus: tenantResponse.status };
  const tenant: CrossTenant = tenantResponse.body;
  const externalId = tenant.tenant_external_id;
  if (!externalId) return { status: 'no-external-id', tenant };

  const stations: CrossChargeBox[] = [];
  for (let page = 0; page < CROSS_MAX_PAGES; page += 1) {
    // eslint-disable-next-line no-await-in-loop
    const response = await fetchJson(`${crossBaseUrl()}${buildPartnerStationsUrl(externalId, page)}`, crossHeaders(externalId));
    if (!response.body) return { status: 'error', httpStatus: response.status };
    const content: CrossChargeBox[] = Array.isArray(response.body) ? response.body : response.body.content || [];
    stations.push(...content);
    // O totalElements do cross é o tamanho da página, não o total: para quando a página vem incompleta.
    if (content.length < CROSS_PAGE_SIZE) return { status: 'ok', tenant, stations, truncated: false };
  }
  return { status: 'ok', tenant, stations, truncated: true };
};
